import React, { ReactElement } from 'react';

import {
  Anchor,
  PII_MASK_CSS_CLASS,
  TableCell,
  TableHeaderCell,
} from '@hcs/design-system';
import { TableCellProps, TableHeaderCellProps } from '@hcs/types';
import {
  PropertyStateArgs,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatCityStateZip, formatStreetAddress } from '@hcs/utils';

import styles from './address.module.css';
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({
  propertyStateArgs,
  linkUrl,
}: {
  propertyStateArgs: PropertyStateArgs | undefined;
  linkUrl?: string;
}) => {
  return (
    <div className={PII_MASK_CSS_CLASS}>
      <div
        className={styles.StreetAddress}
        data-hc-name="address-street-address"
      >
        {linkUrl && propertyStateArgs?.propertyState?.location ? (
          <Anchor
            dataHcName="address-street-address-link"
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formatStreetAddress(propertyStateArgs.propertyState.location)}
          </Anchor>
        ) : (
          formatStreetAddress(propertyStateArgs?.propertyState?.location)
        )}
      </div>
      <div
        className={styles.CityStateZip}
        data-hc-name="address-city-state-zip"
      >
        {formatCityStateZip(propertyStateArgs?.propertyState?.location)}
      </div>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = ({
  noLabel,
  ...props
}: PropertyStateTableHeaderCellProps): ReactElement<TableHeaderCellProps> => {
  return (
    <TableHeaderCell {...props}>
      {noLabel ? null : 'Address'}
      {props.children}
    </TableHeaderCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  width: 200,
  sticky: true,
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  linkUrl,
  children,
  ...tableCellProps
}: {
  propertyStateArgs: PropertyStateArgs | undefined;
  linkUrl?: string;
} & TableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} linkUrl={linkUrl} />
      {children}
    </TableCell>
  );
};

// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  width: 200,
  sticky: true,
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const ADDRESS_CONFIG = {
  HeaderCell,
  ContentCell,
  Display,
  label: 'Address',
  labelShort: 'Address',
};
